import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { Countdown, CustomReminder } from '../types/database';
import { ProUpgradeModal } from './ProUpgradeModal';

const EMOJI_OPTIONS = [
  '🎂', '🎉', '🎊', '🎁', '🎈', '🎄', '🎃', '🎆', '✈️', '🏖️', '🌴', '🌊', '🏔️', '🗼', '🏰', '🎭', '📚', '🎓', '📝', '💼', '👔', '🏆', '⚽', '🏀', '❤️', '💕', '💍', '👶', '🍰', '🎵', '🎸', '🎮', '⭐', '🌟', '✨', '💫', '🌈', '🔔', '⏰', '📅'
]

const COLOR_OPTIONS = [
  { label: 'Coral', value: '#FF6B6B', text: '#fff' },
  { label: 'Rose', value: '#E91E8C', text: '#fff' },
  { label: 'Sage', value: '#6BAF92', text: '#fff' }, // Swapped Peach for Sage
  { label: 'Gold', value: '#F4A435', text: '#fff' },
  { label: 'Orange', value: '#FF8C00', text: '#fff' },
  { label: 'Lavender', value: '#9B8EC4', text: '#fff' },
  { label: 'Sky', value: '#5B9BD5', text: '#fff' },
  { label: 'Blush', value: '#E8A0BF', text: '#fff' },
]

export function CountdownModal({ countdown, onClose }: { countdown?: Countdown | null, onClose: () => void }) {
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState(countdown?.name || '')
  const [date, setDate] = useState(countdown?.event_date ? countdown.event_date.split('T')[0] : '')
  const [iconMode, setIconMode] = useState<'emoji' | 'image'>(countdown?.background_image_url ? 'image' : 'emoji')
  const [selectedEmoji, setSelectedEmoji] = useState(countdown?.emoji || '🎉')
  const [selectedColor, setSelectedColor] = useState(countdown?.color || '#FF6B6B')
  const [category, setCategory] = useState(countdown?.category || 'Personal')
  const [recurring, setRecurring] = useState(countdown?.is_recurring || false)
  const [remind1Day, setRemind1Day] = useState(countdown?.reminders?.day1 || false)
  const [remind1Week, setRemind1Week] = useState(isPro && (countdown?.reminders?.week1 || false))
  const [customReminders, setCustomReminders] = useState<CustomReminder[]>(
    Array.isArray(countdown?.custom_reminders) ? countdown.custom_reminders : []
  )
  const [bgImage, setBgImage] = useState<string | null>(countdown?.background_image_url || null)

  const handleSave = async () => {
    if (!user) return;
    setLoading(true);

    const payload = {
      name: title,
      event_date: date,
      emoji: selectedEmoji,
      color: selectedColor,
      category,
      is_recurring: recurring,
      reminders: { day1: remind1Day, week1: remind1Week },
      custom_reminders: isPro ? customReminders : [],
      background_image_url: bgImage,
      updated_at: new Date().toISOString()
    };

    try {
      if (countdown?.id) {
        const { error } = await supabase.from('countdowns').update(payload).eq('id', countdown.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('countdowns').insert({
          user_id: user.id,
          ...payload
        });
        if (error) throw error;
      }
      onClose();
    } catch (err: any) {
      console.error('Save error:', err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.scrollArea}>
          <div style={styles.field}>
            <label style={styles.label}>Event Name</label>
            <input 
              style={styles.input} 
              placeholder="My Event" 
              value={title} 
              onChange={e => setTitle(e.target.value)} 
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Event Date</label>
            <input 
              type="date" 
              style={styles.input} 
              value={date} 
              onChange={e => setDate(e.target.value)} 
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Icon</label>
            <div style={styles.tabRow}>
              <button 
                style={iconMode === 'emoji' ? styles.tabActive : styles.tab}
                onClick={() => setIconMode('emoji')}
              >
                😊 Emoji
              </button>
              <button 
                style={iconMode === 'image' ? styles.tabActive : styles.tab}
                onClick={() => setIconMode('image')}
              >
                🖼 Image
              </button>
            </div>

            {iconMode === 'emoji' && (
              <div style={styles.emojiGrid}>
                {EMOJI_OPTIONS.map(em => (
                  <button
                    key={em}
                    style={{
                      ...styles.emojiBtn,
                      background: selectedEmoji === em ? '#E8A0BF' : '#f5f0ec',
                      transform: selectedEmoji === em ? 'scale(1.15)' : 'scale(1)',
                    }}
                    onClick={() => setSelectedEmoji(em)}
                  >
                    {em}
                  </button>
                ))}
              </div>
            )}

            {iconMode === 'image' && (
              <div>
                <label style={styles.uploadBox}>
                  {bgImage ? '✅ Image selected — click to change' : '📷 Upload Photo'}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      const fileExt = file.name.split('.').pop()
                      const fileName = `${user.id}/${Date.now()}.${fileExt}`
                      const { error: uploadError } = await supabase.storage
                        .from('countdown-images')
                        .upload(fileName, file, { upsert: true })
                      if (uploadError) {
                        console.error('Upload error:', uploadError)
                        alert('Upload failed. Please try again.')
                        return
                      }
                      const { data: { publicUrl } } = supabase.storage
                        .from('countdown-images')
                        .getPublicUrl(fileName)
                      setBgImage(publicUrl)
                    }}
                  />
                </label>
                {bgImage && (
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={bgImage} alt="preview" style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 10 }} />
                    <button onClick={() => setBgImage(null)} style={styles.removeBtn}>✕ Remove</button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Color</label>
            <div style={styles.colorGrid}>
              {COLOR_OPTIONS.map(c => (
                <button
                  key={c.value}
                  style={{
                    ...styles.colorBtn,
                    background: c.value,
                    color: c.text,
                    outline: selectedColor === c.value ? '3px solid #555' : '3px solid transparent',
                    outlineOffset: '2px',
                  }}
                  onClick={() => setSelectedColor(c.value)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Category</label>
            <select 
              style={styles.input} 
              value={category} 
              onChange={e => setCategory(e.target.value)}
            >
              <option>Birthday</option>
              <option>Holiday</option>
              <option>Travel</option>
              <option>School</option>
              <option>Personal</option>
              <option>Other</option>
            </select>
          </div>

          <div style={styles.checkRow}>
            <input 
              type="checkbox" 
              id="recurring" 
              checked={recurring} 
              onChange={e => setRecurring(e.target.checked)}
              style={styles.checkbox} 
            />
            <label htmlFor="recurring" style={styles.checkLabel}>
              Recurring (repeats every year)
            </label>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Reminders</label>
            <div style={styles.checkRow}>
              <input 
                type="checkbox" 
                id="remind1day" 
                checked={remind1Day} 
                onChange={e => setRemind1Day(e.target.checked)}
                style={styles.checkbox} 
              />
              <label htmlFor="remind1day" style={styles.checkLabel}>
                Remind me 1 day before
              </label>
            </div>
            {isPro ? (
              <>
                <div style={styles.checkRow}>
                  <input 
                    type="checkbox" 
                    id="remind1week" 
                    checked={remind1Week} 
                    onChange={e => setRemind1Week(e.target.checked)}
                    style={styles.checkbox} 
                  />
                  <label htmlFor="remind1week" style={styles.checkLabel}>
                    Remind me 1 week before
                  </label>
                </div>
                {customReminders.map((r, i) => (
                  <div key={i} style={styles.customReminderRow}>
                    <input 
                      type="number" 
                      min="1" 
                      value={r.value} 
                      onChange={e => {
                        const updated = [...customReminders]
                        updated[i] = { ...updated[i], value: parseInt(e.target.value) || 1 }
                        setCustomReminders(updated)
                      }}
                      style={{ ...styles.input, width: '60px', padding: '6px' }} 
                    />
                    <select 
                      value={r.unit} 
                      onChange={e => {
                        const updated = [...customReminders]
                        updated[i] = { ...updated[i], unit: e.target.value as any }
                        setCustomReminders(updated)
                      }}
                      style={{ ...styles.input, flex: 1 }}
                    >
                      <option value="minutes">minutes before</option>
                      <option value="hours">hours before</option>
                      <option value="days">days before</option>
                    </select>
                    <button 
                      onClick={() => setCustomReminders(customReminders.filter((_, idx) => idx !== i))}
                      style={styles.removeBtn}
                    >✕</button>
                  </div>
                ))}
                <button 
                  style={styles.addCustomBtn} 
                  onClick={() => setCustomReminders([...customReminders, { id: Math.random().toString(), value: 1, unit: 'days' }])}
                >
                  + Add Custom Reminder
                </button>
              </>
            ) : (
              <div style={styles.proUpsell}>
                🔒 More reminders & custom alerts — <strong>Pro only</strong>
                <button style={styles.upgradeBtn} onClick={() => setShowUpgradeModal(true)}>Upgrade</button>
              </div>
            )}
          </div>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={styles.saveBtn} onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : (countdown ? 'Update' : 'Add Event')}
          </button>
        </div>
      </div>
      {showUpgradeModal && <ProUpgradeModal onClose={() => setShowUpgradeModal(false)} />}
    </div>
  )
}

const styles: Record<string, any> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: 20,
    width: '90%',
    maxWidth: 480,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
    overflow: 'hidden',
  },
  scrollArea: {
    overflowY: 'auto',
    padding: '24px 24px 8px',
    flex: 1,
  },
  field: { marginBottom: 20 },
  label: {
    display: 'block',
    fontWeight: 600,
    fontSize: 14,
    color: '#333',
    marginBottom: 8,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 14px',
    border: '1.5px solid #e8ddd5',
    borderRadius: 12,
    fontSize: 15,
    background: '#fdfaf8',
    outline: 'none',
    fontFamily: 'inherit',
  },
  tabRow: { display: 'flex', gap: 8, marginBottom: 12 },
  tab: {
    flex: 1,
    padding: '10px',
    border: '1.5px solid #e8ddd5',
    borderRadius: 10,
    background: '#f5f0ec',
    fontSize: 14,
    cursor: 'pointer',
    fontWeight: 500,
  },
  tabActive: {
    flex: 1,
    padding: '10px',
    border: '1.5px solid #E8A0BF',
    borderRadius: 10,
    background: '#5B9BD5',
    color: '#fff',
    fontSize: 14,
    cursor: 'pointer',
    fontWeight: 600,
  },
  emojiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(8,1fr)',
    gap: 6,
  },
  emojiBtn: {
    padding: '8px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    fontSize: 20,
    transition: 'all 0.15s ease',
  },
  colorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4,1fr)',
    gap: 8,
  },
  colorBtn: {
    padding: '12px 6px',
    borderRadius: 12,
    border: 'none',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  checkbox: { width: 18, height: 18, cursor: 'pointer', accentColor: '#E8A0BF' },
  checkLabel: { fontSize: 14, color: '#333', cursor: 'pointer' },
  customReminderRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    marginBottom: 8,
  },
  removeBtn: {
    background: '#f5f0ec',
    border: 'none',
    borderRadius: 8,
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: 12,
    color: '#888',
  },
  addCustomBtn: {
    background: 'none',
    border: '1.5px dashed #E8A0BF',
    color: '#E8A0BF',
    borderRadius: 10,
    padding: '8px 14px',
    fontSize: 13,
    cursor: 'pointer',
    marginTop: 4,
    fontWeight: 500,
    width: '100%'
  },
  proUpsell: {
    background: '#fdf5f9',
    border: '1px dashed #E8A0BF',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 13,
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  upgradeBtn: {
    background: '#E8A0BF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '4px 12px',
    fontSize: 12,
    cursor: 'pointer',
    fontWeight: 600,
  },
  uploadBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #5B9BD5',
    borderRadius: 12,
    padding: '18px',
    color: '#5B9BD5',
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    background: '#f8fbff',
    textAlign: 'center'
  },
  footer: {
    display: 'flex',
    gap: 12,
    padding: '16px 24px',
    borderTop: '1px solid #f0e8e2',
  },
  cancelBtn: {
    flex: 1,
    padding: '14px',
    background: '#f0e8e2',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    color: '#555',
  },
  saveBtn: {
    flex: 2,
    padding: '14px',
    background: '#5B9BD5',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    color: '#fff',
  },
}